import { Country, PaymentMethodsObject, RadioInput } from '../../types'
import { getChilePaymentUrl, initPaymentBolivia } from './index'
import type { Market } from '../../..'
import type { OrderStatus } from '../CommerceLayer'
import type { PaymentTypeConfig } from './countryConfig'
import { findPaymentConfigForCountry, getPaymentTypesForCountry } from './countryConfig'

export type PaymentOption = {
  id: number
  name: string
  icons: string[]
}

export type PaymentMethod = {
  name: string
  options: PaymentOption[]
}

export type CreatePaymentResponse = {
  payment_id: string
  payment_url?: string
  qr?: string
  mercadopago_transaction_id?: string
  mercadopago_preference_id?: string
  mercadopago_init_url?: string
}

export type CreatePaymentResponseYape = {
  message: string
  confirm: {
    data: {
      authorizationNumber: string
    }
    state: number
    message: string
  }
}

export type GetPaymentStatusResponse = {
  status: string
  orderId: string
  amount: string
  qrCode?: string
  qrCodeRaw?: string
  shippingDate?: string
  orderNumber: number
  orderStatus: OrderStatus
}

const validatePaymentMethods = (methods: unknown): methods is PaymentMethod[] => {
  if (!Array.isArray(methods)) return false
  return methods.every(
    (method) =>
      method && typeof method === 'object' && 'name' in method && 'options' in method && Array.isArray(method.options),
  )
}

const isOnlinePaymentMethod = (methodName: string): boolean => {
  return /(Cybersource|Webpay|Flow)/i.test(methodName)
}

const extractIcon = (icons: string[]): string | undefined => icons[0]

const QR_PAYMENT_TYPE_INDEX = 1

type RadioInputWithPriority = RadioInput & { priority?: number }

const sortPaymentOptions = (options: RadioInputWithPriority[]): RadioInput[] => {
  return [...options].sort((a, b) => {
    const priorityA = a.priority ?? 999
    const priorityB = b.priority ?? 999
    if (priorityA !== priorityB) return priorityA - priorityB
    return a.label.localeCompare(b.label, 'es')
  })
}

const isQRMethod = (methodName: string, name: string): boolean => {
  return methodName === 'BGA' || methodName === 'API-Market' || name === 'QR'
}

const processPaymentOption = (
  option: PaymentOption,
  methodName: string,
  country: Country,
): { formattedOptions: RadioInput[]; qrId?: number; priority?: number } => {
  const { id, name, icons } = option
  const config = findPaymentConfigForCountry(name, methodName, country)

  if (isQRMethod(methodName, name)) {
    return { formattedOptions: [], qrId: id }
  }

  if (config) {
    const icon = extractIcon(icons)
    const { priority } = config
    return {
      formattedOptions: config.labels.map((label) => ({ value: id, label, image: icon, priority })),
      priority,
    }
  }

  return {
    formattedOptions: [{ value: id, label: name, image: extractIcon(icons) }],
  }
}

const addPaymentTypesOnce = (
  formattedMethods: PaymentMethodsObject,
  paymentTypesConfig: PaymentTypeConfig[],
  hasAdded: boolean,
): boolean => {
  if (!hasAdded) {
    formattedMethods.types.push(...paymentTypesConfig)
    return true
  }
  return hasAdded
}

const assignQRIDToPaymentType = (types: PaymentTypeConfig[], qrId: number | undefined): PaymentTypeConfig[] => {
  if (qrId === undefined || types.length <= QR_PAYMENT_TYPE_INDEX) return types

  return types.map((type, index) => (index === QR_PAYMENT_TYPE_INDEX ? { ...type, QRID: qrId } : type))
}

export const formatPaymentMethods = (methods: PaymentMethod[], country: Country): PaymentMethodsObject => {
  if (!validatePaymentMethods(methods)) {
    return { types: [], options: [] }
  }

  const formattedMethods: PaymentMethodsObject = { types: [], options: [] }
  const paymentTypesConfig = getPaymentTypesForCountry(country)
  let hasAddedPaymentTypes = false
  let bgaQRID: number | undefined

  methods.forEach(({ options, name: methodName }) => {
    const sortedOptions = [...options].sort((a, b) => a.id - b.id)

    sortedOptions.forEach((option) => {
      if (isOnlinePaymentMethod(methodName)) {
        hasAddedPaymentTypes = addPaymentTypesOnce(formattedMethods, paymentTypesConfig, hasAddedPaymentTypes)
      }

      const { formattedOptions, qrId, priority } = processPaymentOption(option, methodName, country)
      if (qrId) bgaQRID = qrId
      formattedMethods.options.push(...formattedOptions.map((opt) => ({ ...opt, priority })))
    })
  })

  formattedMethods.types = assignQRIDToPaymentType(formattedMethods.types, bgaQRID)
  formattedMethods.options = sortPaymentOptions(formattedMethods.options)

  return formattedMethods
}

function postForm(url: string, data: Record<string, string>) {
  const form = document.createElement('form')
  document.body.appendChild(form)
  form.method = 'post'
  form.action = url
  Object.entries(data).forEach(([key, value]) => {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = key
    input.value = value
    form.appendChild(input)
  })
  form.submit()
}

export interface RedirectToPaymentGatewayParams {
  countryCode: Country
  orderId: string
  currentCity: Market
  sessionId: string
  beforeRedirectCallback?: () => Promise<void>
  sahcUrl: string // sahc stands for secure acceptance hosted checkout
}

export const payInChile = async (params: RedirectToPaymentGatewayParams) => {
  const paymentUrl = await getChilePaymentUrl(params.orderId)
  if (params.beforeRedirectCallback) await params.beforeRedirectCallback()
  window.location.replace(paymentUrl)
}

export const payInBolivia = async (params: RedirectToPaymentGatewayParams) => {
  const formFields = await initPaymentBolivia(params.orderId, params.sessionId, Country.BOLIVIA)
  if (params.beforeRedirectCallback) await params.beforeRedirectCallback()
  postForm(params.sahcUrl, formFields)
}
